/**
 * WorkspaceGroupsTab Component
 *
 * Tab for managing workspace groups
 */

import React, { useState, useEffect } from 'react';
import { Workspace, WorkspaceGroup } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { Users, Trash2, Edit2, Plus } from 'lucide-react';
import { CreateGroupModal } from './CreateGroupModal';
import { EditGroupModal } from './EditGroupModal';
import { GroupMembersModal } from './GroupMembersModal';
import { ConfirmModal } from './ConfirmModal';

interface WorkspaceGroupsTabProps {
  workspace: Workspace;
}

export const WorkspaceGroupsTab: React.FC<WorkspaceGroupsTabProps> = ({ workspace }) => {
  const { loadWorkspaceGroups, deleteWorkspaceGroup } = useWorkspace();
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deletingGroupId, setDeletingGroupId] = useState<number | null>(null);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<WorkspaceGroup | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadGroups = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await loadWorkspaceGroups(workspace.id);
      setGroups(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao carregar grupos');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, [workspace.id]);

  const handleDeleteGroupClick = (group: WorkspaceGroup) => {
    setSelectedGroup(group);
    setShowDeleteConfirm(true);
  };

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    setDeletingGroupId(selectedGroup.id);
    setErrorMessage(null);

    try {
      await deleteWorkspaceGroup(workspace.id, selectedGroup.id);
      setGroups((prev) => prev.filter((g) => g.id !== selectedGroup.id));
      setShowDeleteConfirm(false);
      setSelectedGroup(null);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Falha ao deletar grupo');
    } finally {
      setDeletingGroupId(null);
    }
  };

  const handleEditGroupClick = (group: WorkspaceGroup) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F4CC04]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {(error || errorMessage) && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error || errorMessage}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Grupos do Workspace</h3>
          <p className="text-sm text-[#888888] mt-1">
            Organize membros em grupos ({groups.length} grupo{groups.length !== 1 ? 's' : ''})
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Criar Grupo</span>
        </button>
      </div>

      {/* Groups List */}
      {groups.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-[#888888] mx-auto mb-3" />
          <p className="text-[#888888]">Nenhum grupo neste workspace ainda</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 text-[#F4CC04] hover:text-[#D4B104] font-medium transition-colors"
          >
            Crie seu primeiro grupo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 hover:border-[#444444] transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-[#F4CC04]/20 rounded-lg">
                    <Users className="w-5 h-5 text-[#F4CC04]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">{group.name}</h4>
                    {group.deleted && (
                      <span className="text-xs text-red-400 font-medium">Deletado</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEditGroupClick(group)}
                    disabled={deletingGroupId === group.id || group.deleted}
                    className="p-1 text-[#F4CC04] hover:bg-[#F4CC04]/10 rounded-lg disabled:opacity-50 transition-colors"
                    title="Editar grupo"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  {!group.deleted && (
                    <button
                      onClick={() => handleDeleteGroupClick(group)}
                      disabled={deletingGroupId === group.id}
                      className="p-1 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50 transition-colors"
                      title="Deletar grupo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {group.description && (
                <p className="text-sm text-[#888888] mb-3 line-clamp-2">{group.description}</p>
              )}

              <div className="flex items-center justify-between text-xs text-[#888888] border-t border-[#333333] pt-3">
                <span>Criado em {new Date(group.created_at).toLocaleDateString('pt-BR')}</span>
                <button
                  onClick={() => {
                    setSelectedGroup(group);
                    setShowMembersModal(true);
                  }}
                  className="text-[#F4CC04] hover:text-[#D4B104] font-medium transition-colors"
                >
                  Ver Membros
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        workspace={workspace}
        onSuccess={() => {
          loadGroups();
          setShowCreateModal(false);
        }}
      />

      {/* Edit Group Modal */}
      {selectedGroup && (
        <EditGroupModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedGroup(null);
          }}
          workspace={workspace}
          group={selectedGroup}
          onSuccess={() => {
            loadGroups();
            setShowEditModal(false);
            setSelectedGroup(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedGroup(null);
          setErrorMessage(null);
        }}
        onConfirm={handleDeleteGroup}
        title="Deletar Grupo"
        message={`Tem certeza que deseja deletar o grupo "${selectedGroup?.name}"?\n\nEsta ação não pode ser desfeita.`}
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
        isProcessing={deletingGroupId === selectedGroup?.id}
      />

      {/* Group Members Modal */}
      {selectedGroup && (
        <GroupMembersModal
          isOpen={showMembersModal}
          onClose={() => {
            setShowMembersModal(false);
            setSelectedGroup(null);
          }}
          workspace={workspace}
          group={selectedGroup}
        />
      )}
    </div>
  );
};

export default WorkspaceGroupsTab;
